import { ThreadType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Direction, Encoding, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { GroupParticipant, SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { SMSDataSharedConstants, SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { genUUIDV4 } from '@frontend/string';
import { GroupSendIO } from '../types';
import { UseSendV3MutationEndpointArgs, useSendV3Mutation } from './use-send-v3-mutation';

type MutationContext<C = unknown> = {
  mutationContext:
    | {
        didOptimisticUpdate: false;
        smsId?: never;
        threadId?: never;
        revertV4OptimisticUpdate?: never;
      }
    | {
        didOptimisticUpdate: true;
        smsId: string;
        threadId: string;
        revertV4OptimisticUpdate: () => void;
      };
  otherContext?: C;
};

type GroupSendRequestWithParticipants = Omit<GroupSendIO['input'], 'recipients'> & {
  participants: GroupParticipant[];
};

type NormalizedGroupSendRequest = GroupSendIO['input'] & {
  smsId: string;
};

type ConvertRequestToSMSArgs = {
  request: NormalizedGroupSendRequest;
  threadId: string;
  status?: Status;
  statusDetails?: string;
};

const normalizeParticipantsToSendRecipients = (participants: GroupParticipant[]): GroupSendIO['input']['recipients'] =>
  participants.map((participant) => ({
    // GroupParticipant is the read/display shape, GroupRecipient is the send-v3 wire contract.
    personPhone: participant.phoneNumber ?? '',
    personId: participant.person?.personId,
  }));

const normalizeGroupSendRequest = (request: GroupSendRequestWithParticipants): NormalizedGroupSendRequest => ({
  ...request,
  smsId: request.smsId || genUUIDV4(),
  recipients: normalizeParticipantsToSendRecipients(request.participants),
});

const convertRequestToSMS = ({ request, threadId, status, statusDetails }: ConvertRequestToSMSArgs): SMS => {
  // Note: Many fields are set to empty strings for the following reasons:
  // 1. Not applicable to group MMS: personPhone and personId are empty because group threads have multiple recipients,
  //    not a single person. Similarly, programSlugId and departmentId are not applicable to group MMS sends.
  // 2. Server-assigned fields unknown at optimistic-update time: providerMsgId, readBy, deletedBy, updatedBy, updatedAt,
  //    readAt, deletedAt, and autogeneratedBy will be populated/overwritten when the server response arrives in onSuccess.
  return {
    id: request.smsId,
    providerMsgId: '',
    threadId,
    locationId: request.locationId,
    locationPhone: request.locationPhone,
    personPhone: '',
    direction: Direction.DIRECTION_OUTBOUND,
    body: request.body ?? '',
    status: status || SMSDataSharedConstants.SENDING_STATUS_DATA.status,
    statusDetails: statusDetails || SMSDataSharedConstants.SENDING_STATUS_DATA.statusDetails,
    numMedia: request.media?.length ?? 0,
    media: request.media ?? [],
    encoding: Encoding.ENCODING_UNSPECIFIED,
    segments: 1,
    relatedIds: request.relatedIds ?? [],
    labels: {},
    programSlugId: '',
    personId: '',
    deadline: '',
    autogeneratedBy: '',
    readBy: '',
    deletedBy: '',
    createdBy: request.createdBy ?? '',
    createdAt: new Date().toISOString(),
    updatedBy: '',
    updatedAt: '',
    readAt: '',
    deletedAt: '',
    actionable: false,
    departmentId: '',
    tags: [],
    messageType: request.messageType,
    tagsDetailed: [],
    senderType: request.senderType,
    threadType: ThreadType.THREAD_TYPE_GROUP_MMS,
  };
};

type OtherGroupSendOptions = {
  threadId: string;
};

export const useGroupSendMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends OtherGroupSendOptions = OtherGroupSendOptions,
  RequestOverride extends GroupSendRequestWithParticipants = GroupSendRequestWithParticipants
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseSendV3MutationEndpointArgs<'GroupSend', E, C | undefined, OtherOptions, NormalizedGroupSendRequest> = {}) => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater({ httpOptions: args.httpOptions });

  const mutation = useSendV3Mutation<'GroupSend', E, MutationContext<C>, OtherOptions, NormalizedGroupSendRequest>({
    endpointName: 'GroupSend',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        if (optimisticUpdate && request._otherOptions?.threadId) {
          const newSMS = convertRequestToSMS({ request, threadId: request._otherOptions.threadId });

          const upsertThreadItemResult = await unifiedThreadUpdater.upsertThreadItem({
            matchValues: {
              threadItemId: newSMS.id,
              threadId: newSMS.threadId,
              locationId: newSMS.locationId,
              entityType: EntityType.ENTITY_TYPE_SMS,
            },
            updater: () => ({
              smsMessage: newSMS,
            }),
            isOptimistic: true,
          });

          return {
            mutationContext: {
              didOptimisticUpdate: !!upsertThreadItemResult.revertCacheUpdate,
              smsId: newSMS.id,
              threadId: newSMS.threadId,
              revertV4OptimisticUpdate: upsertThreadItemResult.revertCacheUpdate,
            },
            otherContext: await options?.onMutate?.(request),
          };
        }

        return {
          mutationContext: { didOptimisticUpdate: false },
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: async (response, request, context) => {
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: response.smsId,
            threadId: response.threadId,
            locationId: request.locationId,
            entityType: EntityType.ENTITY_TYPE_SMS,
          },
          updater: (threadItem) => {
            const existingSms =
              threadItem && SMSDataSharedUtils.threadItemsUtils.isSmsMessageItem(threadItem)
                ? threadItem.smsMessage
                : undefined;
            return {
              smsMessage: {
                ...convertRequestToSMS({ request, threadId: response.threadId }),
                ...existingSms,
                status: Status.STATUS_SENT,
                statusDetails: '',
              },
            };
          },
          isOptimistic: false,
        });

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        if (context?.mutationContext.didOptimisticUpdate) {
          context.mutationContext.revertV4OptimisticUpdate();
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) =>
        options?.onSettled?.(response, error, request, context?.otherContext),
    },
  });

  const handleMutate: (
    // `_otherOptions` is part of the UseSchemaMutation contract (not the wire schema) and is
    // required by the optimistic updater to locate the correct cache entry. Exposing it here
    // and in async mutation below avoids callers having to cast the mutate function themselves.
    request: RequestOverride & { _otherOptions?: OtherOptions },
    ...rest: Parameters<typeof mutation.mutate> extends [any, ...infer R] ? R : never
  ) => void = (request, ...rest) => {
    const normalizedRequest = normalizeGroupSendRequest(request as GroupSendRequestWithParticipants);
    return mutation.mutate(normalizedRequest, ...rest);
  };

  const handleMutateAsync: (
    request: RequestOverride & { _otherOptions?: OtherOptions },
    ...rest: Parameters<typeof mutation.mutateAsync> extends [any, ...infer R] ? R : never
  ) => Promise<Awaited<ReturnType<typeof mutation.mutateAsync>>> = (request, ...rest) => {
    const normalizedRequest = normalizeGroupSendRequest(request as GroupSendRequestWithParticipants);
    return mutation.mutateAsync(normalizedRequest, ...rest);
  };

  return {
    ...mutation,
    mutate: handleMutate,
    mutateAsync: handleMutateAsync,
  };
};
